/**
 * Role-Based Access Control Types
 */

// Role names matching backend
export const ROLES = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  MANAGER: "manager",
} as const;

export type RoleName = (typeof ROLES)[keyof typeof ROLES];

// Module/Feature names matching your routes
export const MODULES = {
  DASHBOARD: "dashboard",

  // Crop & Disease
  CROP: "crop",
  CROP_CATEGORY: "crop_category",
  DISEASE_INSECT: "disease_insect",
  DISEASE_DETECTION: "disease_detection",

  // Product
  PRODUCT: "product",
  PRODUCT_CATEGORY: "product_category",
  PRODUCT_SUB_CATEGORY: "product_sub_category",
  PRODUCT_COMPANY: "product_company",
  PRODUCT_ELEMENT: "product_element",
  PRODUCT_SOLUTION: "product_solution",
  PRODUCT_SIZE: "product_size",
  PRODUCT_UNIT: "product_unit",
  PRODUCT_COLOR_CODE: "product_color_code",
  GENERIC_GROUP: "generic_group",
  WORKING_MOOD: "working_mood",

  // Order
  ORDER: "order",
  ORDER_PENDING: "order_pending",
  ORDER_APPROVED: "order_approved",
  ORDER_COMPLETE: "order_complete",
  ORDER_CANCELLED: "order_cancelled",
  ORDER_RETURN_REFUND: "order_return_refund",
  ORDER_MANUAL: "order_manual",

  // Stock
  STOCK: "stock",

  // Early Warning
  WARNING_CATEGORY: "warning_category",
  EARLY_WARNING: "early_warning",

  // Course
  COURSE: "course",
  COURSE_CATEGORY: "course_category",

  // Dealer
  DEALER: "dealer",
  PENDING_DEALER: "pending_dealer",
  PAYMENT_METHOD_APPROVED: "payment_method_approved",
  PAYMENT_METHOD_PENDING: "payment_method_pending",

  // Configuration
  BANNER: "banner",
  CHAD_BAGAN_TIPS: "chad_bagan_tips",
  FAQ: "faq",
  FARMER_SAFETY: "farmer_safety",
  FEATURE: "feature",
  GALLERY: "gallery",
  PROFESSION: "profession",
  REFERENCE: "reference",
  TEAM_CONSULTANT: "team_consultant",
  DISTRICT: "district",
  DIVISION: "division",
} as const;

export type ModuleName = (typeof MODULES)[keyof typeof MODULES];

// Permission action types
export const PERMISSION_ACTIONS = {
  VIEW: "view",
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
  APPROVE: "approve",
  EXPORT: "export",
} as const;

export type PermissionAction =
  (typeof PERMISSION_ACTIONS)[keyof typeof PERMISSION_ACTIONS];

// Module permission configuration
export interface ModulePermission {
  module: ModuleName;
  requiredPermissions?: string[]; // Backend permission names required
  allowedRoles?: RoleName[]; // If not specified, defaults to all roles
  actions?: {
    [key in PermissionAction]?: string[]; // Map actions to backend permissions
  };
}

// Route configuration
export interface RoutePermission {
  path: string;
  module: ModuleName;
  requiredPermissions?: string[];
  allowedRoles?: RoleName[];
}

// Menu item configuration
export interface MenuPermission {
  key: string;
  module: ModuleName;
  requiredPermissions?: string[];
  allowedRoles?: RoleName[];
}
